import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save, ChevronDown, ChevronUp, DownloadCloud, FileCode, Import, Info } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/services/adminApi";

const inputCls = "px-2 py-1 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50";

function WgRow({ ch, onReload }) {
  const [form, setForm] = useState({ siteini: ch.siteini || "", site_id: ch.site_id || "", xmltv_id: ch.xmltv_id || "" });
  const [saving, setSaving] = useState(false);
  const dirty = form.siteini !== (ch.siteini || "") || form.site_id !== (ch.site_id || "") || form.xmltv_id !== (ch.xmltv_id || "");

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await adminApi.updateWgChannel(ch.id, { ...form, ...extra });
      toast.success("Yadda saxlandı");
      onReload();
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm(`"${ch.name}" WG siyahısından silinsin?`)) return;
    await adminApi.deleteWgChannel(ch.id);
    onReload();
  };

  return (
    <div className={`grid grid-cols-[minmax(140px,1.2fr)_1fr_1fr_1fr_auto] gap-2 items-center px-3 py-2 rounded-lg ${ch.enabled ? "bg-zinc-900/60" : "bg-zinc-900/30 opacity-60"}`} data-testid={`wg-channel-${ch.id}`}>
      <div className="min-w-0">
        <p className="text-xs font-semibold truncate">{ch.name}</p>
        {ch.notes && <p className="text-[10px] text-zinc-500 truncate" title={ch.notes}>{ch.notes}</p>}
      </div>
      <input placeholder="siteini (məs: tvplus.com.tr)" value={form.siteini} onChange={(e) => setForm({ ...form, siteini: e.target.value })} className={inputCls} data-testid={`wg-siteini-${ch.id}`} />
      <input placeholder="site_id" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inputCls} data-testid={`wg-siteid-${ch.id}`} />
      <input placeholder="xmltv_id" value={form.xmltv_id} onChange={(e) => setForm({ ...form, xmltv_id: e.target.value })} className={inputCls} />
      <div className="flex items-center gap-1">
        <button onClick={() => save({ enabled: !ch.enabled })} className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${ch.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`} data-testid={`wg-toggle-${ch.id}`}>
          {ch.enabled ? "Aktiv" : "Deaktiv"}
        </button>
        <button onClick={() => save()} disabled={saving || !dirty} className={`p-1.5 rounded-lg transition-colors ${dirty ? "bg-amber-500/20 text-amber-400" : "bg-zinc-800 text-zinc-600"}`} title="Yadda saxla" data-testid={`wg-save-${ch.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </button>
        <button onClick={del} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" title="Sil">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function WebGrabSection({ busy, onImportStarted }) {
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [days, setDays] = useState(3);
  const [lic, setLic] = useState({ wg_username: "", wg_email: "", wg_password: "", wg_license_force: false });
  const [guide, setGuide] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => {
    adminApi.wgChannels().then(({ data }) => setChannels(data.channels)).catch(() => {});
    adminApi.wgSettings().then(({ data }) => { setDays(data.days); setLic({ wg_username: data.wg_username || "", wg_email: data.wg_email || "", wg_password: data.wg_password || "", wg_license_force: !!data.wg_license_force }); }).catch(() => {});
    adminApi.wgGuideStatus().then(({ data }) => setGuide(data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open]);

  const saveDays = async () => {
    try {
      await adminApi.updateWgSettings({ days: Number(days) });
      toast.success("Parametrlər yadda saxlandı");
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveLic = async () => {
    try {
      await adminApi.updateWgSettings(lic);
      toast.success("Lisenziya yadda saxlandı (config yeniləndi)");
    } catch (err) { toast.error(apiError(err)); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await adminApi.seedWgDefaults();
      toast.success(`${data.added} kanal əlavə olundu, ${data.skipped} mövcud idi`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    setSeeding(false);
  };

  const addChannel = async () => {
    if (!newName.trim()) return;
    try {
      await adminApi.createWgChannel({ name: newName.trim(), enabled: false });
      setNewName("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const importGuide = async () => {
    try {
      await adminApi.importWgGuide();
      toast.success("guide.xml import olunur...");
      onImportStarted();
    } catch (err) { toast.error(apiError(err)); }
  };

  const activeCount = channels.filter((c) => c.enabled && c.siteini).length;
  const fmtDt = (iso) => iso ? new Date(iso).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl mb-5" data-testid="wg-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left" data-testid="wg-section-toggle">
        <div className="flex items-center gap-3">
          <DownloadCloud className="w-5 h-5 text-sky-400" />
          <div>
            <p className="font-semibold text-sm">WebGrab+Plus (WG++) — niş kanallar</p>
            <p className="text-xs text-zinc-500">{channels.length ? `${channels.length} kanal konfiqurasiyada, ${activeCount} aktiv` : "EPG-si tapılmayan kanallar üçün siteini grabber"}</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex items-start gap-2 bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 text-xs text-zinc-400">
            <Info className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
            <p>WG++ ayrıca Docker konteynerdə <span className="font-mono text-sky-300">(docker-compose: webgrab servisi)</span> serverinizdə işləyir. Bu paneldə kanal siyahısını idarə etdikcə <span className="font-mono text-sky-300">WebGrab++.config.xml</span> avtomatik yenilənir və paylaşılan volume-a yazılır. WG++ hər gecə grab edir, çıxış <span className="font-mono text-sky-300">guide.xml</span> saat 03:00-da Hub-a avtomatik import olunur.</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-400">EPG günləri:</label>
              <input type="number" min="1" max="14" value={days} onChange={(e) => setDays(e.target.value)} className={`${inputCls} w-16`} data-testid="wg-days-input" />
              <button onClick={saveDays} className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs transition-colors" data-testid="wg-days-save">Saxla</button>
            </div>
            <button onClick={seed} disabled={seeding} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/15 hover:bg-violet-500/25 text-violet-400 rounded-lg text-xs transition-colors disabled:opacity-50" data-testid="wg-seed-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Defolt siyahını yüklə (39 kanal)
            </button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/wg/config`} target="_blank" rel="noreferrer"
              onClick={(e) => { e.preventDefault(); const t = localStorage.getItem("admin_token"); fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/wg/config`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.text()).then(x => { const w = window.open("", "_blank"); w.document.write(`<pre>${x.replace(/</g, "&lt;")}</pre>`); }); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs transition-colors" data-testid="wg-config-link">
              <FileCode className="w-3.5 h-3.5" /> Config.xml-ə bax
            </a>
            <button onClick={importGuide} disabled={busy || !guide?.exists} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 rounded-lg text-xs transition-colors disabled:opacity-40" data-testid="wg-import-btn">
              <Import className="w-3.5 h-3.5" /> guide.xml-i indi import et
            </button>
            <span className="text-[11px] text-zinc-500" data-testid="wg-guide-status">
              guide.xml: {guide?.exists ? `${(guide.size / 1024).toFixed(0)} KB · ${fmtDt(guide.modified)}` : "hələ yaradılmayıb"}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap bg-zinc-900/80 border border-zinc-800 rounded-lg p-3" data-testid="wg-license-row">
            <span className="text-xs text-zinc-400 font-semibold">WG++ Lisenziya (donator):</span>
            <input placeholder="istifadəçi adı" value={lic.wg_username} onChange={(e) => setLic({ ...lic, wg_username: e.target.value })} className={`${inputCls} w-36`} data-testid="wg-lic-username" />
            <input placeholder="e-poçt" value={lic.wg_email} onChange={(e) => setLic({ ...lic, wg_email: e.target.value })} className={`${inputCls} w-44`} data-testid="wg-lic-email" />
            <input type="password" placeholder="lisenziya şifrəsi" value={lic.wg_password} onChange={(e) => setLic({ ...lic, wg_password: e.target.value })} className={`${inputCls} w-36`} data-testid="wg-lic-password" />
            <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer" title="İlk aktivasiya üçün 1 dəfə yandırın, aktivasiyadan sonra söndürün">
              <input type="checkbox" checked={lic.wg_license_force} onChange={(e) => setLic({ ...lic, wg_license_force: e.target.checked })} data-testid="wg-lic-force" />
              Force aktivasiya
            </label>
            <button onClick={saveLic} className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs transition-colors" data-testid="wg-lic-save">Saxla</button>
            <span className="text-[10px] text-zinc-500">🔒 işarəli siteini-lər (tvplus, dsmart, aljazeera, sky.it, movistarplus) V3-şifrəlidir — <a href="https://webgrabplus.com/donate" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">donator lisenziyası</a> olmadan skip olunur. Limitlər: default 2 siteini/20 kanal, donator 15 siteini/250 kanal.</span>
          </div>

          <div className="flex gap-2">
            <input placeholder="Yeni kanal adı" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addChannel()} className={`${inputCls} w-56`} data-testid="wg-new-channel-input" />
            <button onClick={addChannel} className="flex items-center gap-1 px-3 py-1 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs transition-colors" data-testid="wg-add-channel-btn">
              <Plus className="w-3.5 h-3.5" /> Əlavə et
            </button>
          </div>

          {channels.length > 0 && (
            <div className="space-y-1">
              <div className="grid grid-cols-[minmax(140px,1.2fr)_1fr_1fr_1fr_auto] gap-2 px-3 text-[10px] text-zinc-500 uppercase">
                <span>Kanal</span><span>siteini</span><span>site_id</span><span>xmltv_id</span><span></span>
              </div>
              {channels.map((ch) => <WgRow key={ch.id} ch={ch} onReload={load} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
